"use strict";
/** Copyright (c) 2025 DAIA Tech Pvt Ltd. All rights reserved.
 *
 * This software is confidential and proprietary information of DAIA Tech Pvt Ltd
 * and its licensors. You shall not disclose such Confidential Information and shall
 * use it only in accordance with the terms of the license agreement.
 *
 * Unauthorized reproduction, modification, distribution, or disclosure of this
 * software is strictly prohibited.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.createTaskSchema = exports.createTaskForm = exports.taskTypeForm = exports.TaskKinds = exports.taskTypes = void 0;
const parameter_specs_1 = require("@karya/parameter-specs");
const Index_1 = require("../../languages/Index");
const Index_2 = require("../../policy/Index");
const ProportionalPayments_1 = require("./ProportionalPayments");
exports.taskTypes = {
    MANUAL: 'Manual Task',
    AUTO: 'Auto Task',
};
exports.TaskKinds = {
    AUDIO_RECORDING: 'Audio Recording',
    CONVERSATION_RECORDING: 'Conversation Recording',
    IMAGE_ANNOTATION: 'Image Annotation',
    IMAGE_CAPTURE: 'Image Capture',
    ID_CAPTURE: 'ID Capture',
    SEGMENTATION: 'Segmentation',
    TRANSCRIPTION: 'Transcription',
    TRANSLATION: 'Translation',
    VALIDATION: 'Validation',
    SURVEY: 'Survey',
    EVALUATION: 'Evaluation',
    VIDEO_RECORDING: 'Video Recording',
    COORDINATION: 'Coordination',
    CONFERENCER: 'Conferencer',
    OTHER: 'Other',
};
exports.taskTypeForm = [
    {
        label: 'Task Type',
        description: 'Choose the type of task. Manual tasks involve some user component can be shipped to users, whereas auto tasks are entirely run in the server',
        required: true,
        parameters: [
            {
                id: 'taskType',
                label: 'Task Type',
                description: '',
                type: 'enum',
                list: exports.taskTypes,
                initial: 'MANUAL',
                required: true,
            },
        ],
    },
];
exports.createTaskForm = [
    {
        label: 'Basic Task Information',
        description: 'Provide some basic information about the task you are about to create',
        required: true,
        parameters: [
            {
                id: 'name',
                label: 'Task Name',
                description: 'Name of the task in English. This will be displayed only in the task dashboard',
                type: 'string',
                required: true,
            },
            {
                id: 'description',
                label: 'Task Description',
                description: 'Detailed description of the task.',
                type: 'string',
                long: true,
                required: true,
            },
            {
                id: 'localizeTo',
                label: 'Localize To',
                description: 'Languages that you want the task to be available in',
                type: 'enum_multi',
                list: Index_1.languagesList,
                required: false,
            },
            {
                id: 'display_name',
                label: 'Display Name',
                description: 'Name of the task to be displayed on the worker application to the worker',
                type: 'json_object',
                renderer: 'localized-string',
                required: true,
            },
            {
                id: 'tags',
                label: 'Task Tags',
                description: 'A set of string tokens to associate with this task. These tokens must uniquely identify this task or a set of related tasks',
                type: 'list',
                required: true,
            },
            {
                id: 'taskKind',
                label: 'Task Kind',
                description: 'The kind of task you are creating',
                type: 'enum_multi',
                list: exports.TaskKinds,
                required: true,
                validator: 'taskKindSpecValidator',
            },
            {
                id: 'showTaskIn',
                label: 'Show Task In',
                description: 'The platforms where the task should be shown',
                type: 'enum',
                list: {
                    ANDROID: 'Android',
                    WEB: 'Web',
                    ANDROID_AND_WEB: 'Android and Web',
                },
                default: 'ANDROID_AND_WEB',
                required: false,
            },
            {
                id: 'isValidationTask',
                label: 'Validation Task',
                description: 'Indicates that this task is a validation task for assignments of another task. In this case, the output of the task is required to have two parameters: an accepted flag that indicates if the source assignment response is accepted, and a fraction score indicating what fraction of the bonus credits should be paid out to the user',
                type: 'boolean',
                required: false,
            },
            {
                id: 'isLearnTask',
                label: 'Learn Task',
                description: 'Indicates that this task is a learning task. Workers earn a fixed number of stars upon successfully completing the task.',
                type: 'boolean',
                required: false,
            },
            {
                id: 'stars',
                label: 'Stars',
                description: 'Number of stars that can be earned for completing this learn task',
                type: 'int',
                min: 1,
                required: false,
                requires: [['isLearnTask', true]],
                requiredIfSatisfied: true,
            },
            {
                id: 'duration.min',
                label: 'Minimum Duration (minutes)',
                description: 'Minimum time to complete this learn task in minutes',
                type: 'int',
                min: 1,
                required: false,
                requires: [['isLearnTask', true]],
                requiredIfSatisfied: true,
            },
            {
                id: 'duration.max',
                label: 'Maximum Duration (minutes)',
                description: 'Maximum time to complete this learn task in minutes',
                type: 'int',
                min: 1,
                required: false,
                requires: [['isLearnTask', true]],
                requiredIfSatisfied: true,
            },
            {
                id: 'retryDelayDays',
                label: 'Retry Delay (days)',
                description: 'Number of days after which a failed worker can retry the entire task. Leave empty to disable retries.',
                type: 'int',
                min: 1,
                required: false,
                requires: [['isLearnTask', true]],
            },
            {
                id: 'maxTaskRetries',
                label: 'Max Task Retries',
                description: 'Maximum number of times a worker can retry this task. Leave empty for unlimited retries.',
                type: 'int',
                min: 1,
                required: false,
                requires: [['isLearnTask', true]],
            },
        ],
    },
    {
        label: 'Payment Parameters',
        description: 'These parameters determine when a user is going to paid and how much. 1 credit = 1 paise',
        required: true,
        parameters: [
            {
                id: 'baseCreditsPerMicrotask',
                label: 'Base Credits per Microtask',
                description: 'This is the amount of credits a worker will get regardless of the quality of work.',
                type: 'int',
                required: true,
            },
            {
                id: 'enableProportionalBasePayments',
                label: 'Enable Proportional Base Payments',
                description: 'When enabled, base credits are flat base per microtask plus credits per unit times a numeric value from microtask input or assignment output (optional max caps the total).',
                type: 'boolean',
                required: false,
            },
            {
                id: 'proportionalBase.source',
                label: 'Base Value Source',
                description: 'Where to read the numeric value used for base credit computation.',
                type: 'enum',
                list: ProportionalPayments_1.proportionalValueSources,
                required: false,
                requires: [['enableProportionalBasePayments', true]],
            },
            {
                id: 'proportionalBase.key',
                label: 'Base Numeric Key',
                description: 'Key in input.data or output.data holding the numeric value for base credits.',
                type: 'string',
                required: false,
                requires: [['enableProportionalBasePayments', true]],
            },
            {
                id: 'proportionalBase.creditsPerUnit',
                label: 'Base Credits per Unit',
                description: 'Variable base = floor(numeric_value × this rate), added to base credits per microtask.',
                type: 'float',
                required: false,
                requires: [['enableProportionalBasePayments', true]],
            },
            {
                id: 'proportionalBase.maxCredits',
                label: 'Max Base Credits',
                description: 'Optional cap on total base credits (flat plus variable).',
                type: 'int',
                required: false,
                requires: [['enableProportionalBasePayments', true]],
            },
            {
                id: 'bonusCreditsPerMicrotask',
                label: 'Bonus Credits per Microtask',
                description: 'This is the maximum amount of credits a worker will get post validation of their work. The actual amount will depend on the validation parameters and the quality of the work.',
                type: 'int',
                required: true,
            },
            {
                id: 'enableProportionalBonusPayments',
                label: 'Enable Proportional Bonus Payments',
                description: 'When enabled, max bonus credits are bonus per microtask plus credits per unit times a numeric value from microtask input or assignment output (optional max caps the total).',
                type: 'boolean',
                required: false,
            },
            {
                id: 'proportionalBonus.source',
                label: 'Bonus Value Source',
                description: 'Where to read the numeric value used for bonus credit computation.',
                type: 'enum',
                list: ProportionalPayments_1.proportionalValueSources,
                required: false,
                requires: [['enableProportionalBonusPayments', true]],
            },
            {
                id: 'proportionalBonus.key',
                label: 'Bonus Numeric Key',
                description: 'Key in input.data or output.data holding the numeric value for bonus credits.',
                type: 'string',
                required: false,
                requires: [['enableProportionalBonusPayments', true]],
            },
            {
                id: 'proportionalBonus.creditsPerUnit',
                label: 'Bonus Credits per Unit',
                description: 'Variable max bonus = floor(numeric_value × this rate), added to bonus per microtask; scaled by validation at verify time.',
                type: 'float',
                required: false,
                requires: [['enableProportionalBonusPayments', true]],
            },
            {
                id: 'proportionalBonus.maxCredits',
                label: 'Max Bonus Credits',
                description: 'Optional cap on total max bonus credits (flat plus variable).',
                type: 'int',
                required: false,
                requires: [['enableProportionalBonusPayments', true]],
            },
            {
                id: 'enableAudioCredits',
                label: 'Enable Audio Duration-Based Credits',
                description: 'Enable this for audio tasks where worker pay should be calculated from the duration of the audio clip rather than a flat per-microtask rate.',
                type: 'boolean',
                required: false,
            },
            {
                id: 'bonusCreditsPerSecond',
                label: 'Bonus Credits per Second of Audio (paise)',
                description: 'Bonus credits paid to the worker will be computed as floor(duration_seconds × this_rate).',
                type: 'int',
                required: false,
                requires: [['enableAudioCredits', true]],
            },
            {
                id: 'bonusAudioDurationKey',
                label: 'Bonus Audio Duration Key',
                description: 'The exact key inside microtask input.data that holds the audio duration in seconds for bonus credit computation (e.g. "duration_sec"). Required when Bonus Credits per Second is set.',
                type: 'string',
                required: false,
                requires: [['enableAudioCredits', true]],
            },
            {
                id: 'maxBonusAudioCredits',
                label: 'Max Bonus Audio Credits per Assignment (paise)',
                description: 'Optional upper limit on bonus credits computed from audio duration. If the computed floor(duration × rate) exceeds this value, the worker is paid this amount instead.',
                type: 'int',
                required: false,
                requires: [['enableAudioCredits', true]],
            },
            {
                id: 'baseCreditsPerSecond',
                label: 'Base Credits per Second of Audio (paise)',
                description: 'Guaranteed base credits paid to the worker will be computed as floor(duration_seconds × this_rate).',
                type: 'int',
                required: false,
                requires: [['enableAudioCredits', true]],
            },
            {
                id: 'baseAudioDurationKey',
                label: 'Base Audio Duration Key',
                description: 'The exact key inside microtask input.data that holds the audio duration in seconds for base credit computation (e.g. "duration_sec"). Required when Base Credits per Second is set.',
                type: 'string',
                required: false,
                requires: [['enableAudioCredits', true]],
            },
            {
                id: 'maxBaseAudioCredits',
                label: 'Max Base Audio Credits per Assignment (paise)',
                description: 'Optional upper limit on base credits computed from audio duration. If the computed floor(duration × rate) exceeds this value, the worker is paid this amount instead.',
                type: 'int',
                required: false,
                requires: [['enableAudioCredits', true]],
            },
        ],
    },
    {
        label: 'Referral Parameters',
        description: 'These parameters define the referral policy for the task',
        required: true,
        parameters: [
            {
                id: 'enableReferral',
                label: 'Enable Referral',
                description: 'If enabled, the task will have a referral policy',
                type: 'boolean',
                required: false,
            },
            {
                id: 'referral.message',
                label: 'Referral Message',
                description: 'Message to be shown to the user to refer the task to others',
                type: 'string',
                required: false,
                requires: [['enableReferral', true]],
                requiredIfSatisfied: true,
            },
            {
                id: 'referral.videoName',
                label: 'Referral Video Name',
                description: 'Name of the referral video',
                type: 'file',
                required: false,
                requires: [['enableReferral', true]],
                requiredIfSatisfied: false,
            },
            {
                id: 'referral.ageBand',
                label: 'Age Band',
                description: 'Age Band for the user',
                type: 'json_array',
                items: [
                    {
                        id: 'min',
                        label: 'Minimum Age',
                        description: 'Minimum age of the referred user',
                        min: 18,
                        type: 'int',
                        required: true,
                    },
                    {
                        id: 'max',
                        label: 'Maximum Age',
                        description: 'Maximum age of the referred user',
                        min: 18,
                        type: 'int',
                        required: true,
                    },
                ],
                required: false,
                requires: [['enableReferral', true]],
                requiredIfSatisfied: true,
            },
            {
                id: 'referral.genderCriteria',
                label: 'Gender Criteria',
                description: 'Referral is only valid for users that identify with these genders',
                type: 'enum_multi',
                list: {
                    MALE: 'Male',
                    FEMALE: 'Female',
                    NOT_SHARED: 'Not Shared',
                },
                required: false,
                requires: [['enableReferral', true]],
                requiredIfSatisfied: true,
            },
        ],
    },
    {
        label: 'Completion Policy',
        description: 'These parameters define when a microtask of the task is deemed complete.',
        required: true,
        parameters: Index_2.completionPolicyParameters,
    },
    {
        label: 'Test Evaluation Policy',
        description: 'These parameters define how test assignments are evaluated',
        required: false,
        parameters: [
            {
                id: 'enableTestTask',
                label: 'Enable Test Task',
                description: 'If enabled, there will be a test phase before the main task',
                type: 'boolean',
                required: false,
            },
            {
                id: 'maxTestAssignmentsPerUser',
                label: 'Maximum Test Assignments Per User',
                description: 'Maximum number of test assignments per user',
                type: 'int',
                required: false,
                requires: [['enableTestTask', true]],
                requiredIfSatisfied: true,
            },
            {
                id: 'minAcceptedTestResponsesPerUser',
                label: 'Minimum Accepted Test Responses Per User',
                description: 'Minimum number of accepted test responses per user',
                type: 'int',
                required: false,
                requires: [['enableTestTask', true]],
                requiredIfSatisfied: true,
            },
            {
                id: 'maxTestReassignmentsPerUser',
                label: 'Maximum Test Reassignments Per User',
                description: 'Number of times a test microtask can be reassigned to a user',
                type: 'int',
                required: false,
                requires: [['enableTestTask', true]],
                requiredIfSatisfied: true,
            },
            {
                id: 'maxTestAssignmentsPerMicrotask',
                label: 'Maximum Test Assignments Per Microtask',
                description: 'Maximum number of distinct users that each test microtask can be assigned to. Independent of the main phase Completion Policy. If left empty, there is no cap during the test phase, so every test microtask can be handed out to any number of users.',
                type: 'int',
                required: false,
                requires: [['enableTestTask', true]],
            },
            {
                id: 'testMicrotaskAssignmentOrder',
                label: 'Test Microtask Assignment Order',
                description: 'Order in which test microtasks are assigned during the test phase. Independent of the main phase assignment order on the task assignment.',
                type: 'enum',
                list: Index_2.microtaskAssignmentOrders,
                required: false,
                requires: [['enableTestTask', true]],
            },
        ],
    },
    {
        label: 'Dataset Parameters',
        description: 'These parameters define the input and output datasets for the task',
        required: true,
        parameters: [
            {
                id: 'referenceDatasetId',
                label: 'Select Reference Dataset',
                description: 'Select the input dataset you want to use for reference',
                type: 'enum',
                list: {},
                required: false,
            },
            {
                id: 'subscribeToReferenceDataset',
                label: 'Subscribe to Reference Dataset',
                description: 'If enabled, the task subscribed to the reference dataset',
                type: 'boolean',
                required: false,
            },
            {
                id: 'createNewInputDatasets',
                label: 'Create New Input Datasets',
                description: 'If enabled, the task will be subscribed to newly created input datasets',
                type: 'boolean',
                required: false,
            },
            {
                id: 'inputDatasets',
                label: 'Input Datasets',
                description: 'The input datasets to be created for the task',
                type: 'json_array',
                renderer: 'input-dataset-editable-list',
                required: false,
                requires: [['createNewInputDatasets', true]],
                requiredIfSatisfied: true,
            },
            {
                id: 'outputGenGranularity',
                label: 'Output Generation Granularity',
                description: 'This parameter determines if the output should be generated for each microtask or for a response of each microtask. For example, if you are generating multiple audio recordings for a given prompt, then you must set this to each response. On the other hand, it you are transcribing an audio file, then there is only one output, and you must set the value to microtask.',
                type: 'fixed',
                value: 'ASSIGNMENT',
                required: false,
            },
            {
                id: 'outputDatasetName',
                label: 'Output Dataset Name',
                description: 'The name of the output dataset where the data generated from this task will be stored',
                type: 'string',
                required: true,
            },
            {
                id: 'enableOutputDatapointInputKeysFilter',
                label: 'Filter Input Keys in Output Datapoints',
                description: 'Enable this to selectively choose which input keys to include in output datapoints. This helps reduce output datapoint size by excluding unnecessary input keys. When disabled, all input keys are included.',
                type: 'boolean',
                required: false,
            },
            {
                id: 'outputDatapointInputKeys',
                label: 'Select Input Keys to Include',
                description: 'Specify which input keys to include in output datapoints. Example: {"data": ["sentence_id", "text"], "files": ["audio"]}. Leave data or files empty to exclude all keys from that section.',
                type: 'json',
                required: false,
                requires: [['enableOutputDatapointInputKeysFilter', true]],
                requiredIfSatisfied: true,
            },
            {
                id: 'requireRepeatingMicrotask',
                label: 'Enable duplication of microtask for during subscription',
                description: 'If enabled, the microtask will be repeated until the worker submits a valid response',
                type: 'boolean',
                required: false,
            },
        ],
    },
    {
        label: 'Task Assignment For Main Task',
        description: 'Automatically create task assignment for the main task',
        required: true,
        parameters: [
            {
                id: 'allowSkipping',
                label: 'Allow Skipping',
                description: 'Allow users to skip microtasks',
                type: 'boolean',
                required: false,
            },
            {
                id: 'allowEdit',
                label: 'Allow Editing',
                description: 'Allow users to edit microtasks',
                type: 'boolean',
                required: false,
            },
            {
                id: 'createTaskAssignments',
                label: 'Automatically create task assignment for main task',
                type: 'boolean',
                required: false,
            },
            {
                id: 'taskAssignments',
                label: 'Task Assignments',
                description: 'The task assignments to be created for the task',
                type: 'json_array',
                renderer: 'task-assignment-editable-list',
                required: false,
                requires: [['createTaskAssignments', true]],
                requiredIfSatisfied: true,
            },
        ],
    },
    {
        label: 'Main Task Specifications',
        description: 'This section specifies all the task specifications for the main task',
        required: false,
        parameters: [
            {
                id: 'spec',
                label: 'Main Task Specification',
                description: 'Import task specification from a file',
                type: 'json_object',
                renderer: 'task-builder-modal',
                required: true,
                extraProps: {
                    kind: 'MAIN',
                },
            },
            {
                id: 'enableWorkerPromise',
                label: 'Enable Worker Promise Gating',
                description: 'When enabled, workers must accept a Worker Promise before accessing this task in the app. It is checked by default while creating the first task of a project.',
                type: 'boolean',
                required: false,
            },
            {
                id: 'customIntroEnabled',
                label: 'Customize Introduction Flow',
                type: 'boolean',
                required: false,
            },
            {
                id: 'introSpec',
                label: 'Task Introduction Specification',
                type: 'json_object',
                renderer: 'task-builder-modal',
                required: false,
                requires: [['customIntroEnabled', true]],
                requiredIfSatisfied: true,
                extraProps: {
                    kind: 'INTRO',
                },
            },
            {
                id: 'tutorialAvailable',
                label: 'Enable Tutorial',
                type: 'boolean',
                required: false,
            },
            {
                id: 'tutorialSpec',
                label: 'Tutorial Specification',
                description: 'A constant task specification that guides the user through the task',
                type: 'json_object',
                renderer: 'task-builder-modal',
                required: false,
                requires: [['tutorialAvailable', true]],
                requiredIfSatisfied: true,
                extraProps: {
                    kind: 'TUTORIAL',
                },
            },
            {
                id: 'surveyAvailable',
                label: 'Enable Survey',
                type: 'boolean',
                required: false,
            },
            {
                id: 'surveyOnTestTaskFailure',
                label: 'Show Survey on Test Task Failure',
                description: 'When enabled, workers who fail the test task will be redirected to the survey',
                type: 'boolean',
                required: false,
                requires: [['surveyAvailable', true]],
            },
            {
                id: 'surveySpec',
                label: 'Survey Specification',
                description: 'A constant task specification that collects survey responses from the user',
                type: 'json_object',
                renderer: 'task-builder-modal',
                required: false,
                requires: [['surveyAvailable', true]],
                requiredIfSatisfied: true,
                extraProps: {
                    kind: 'SURVEY',
                },
            },
            {
                id: 'requireConfig',
                label: 'Require User Configuration',
                type: 'boolean',
                required: false,
            },
            {
                id: 'configSpec',
                label: 'Task Config Specification',
                description: 'A specification that gets the required configuration for the task',
                type: 'json_object',
                renderer: 'task-builder-modal',
                required: false,
                requires: [['requireConfig', true]],
                requiredIfSatisfied: true,
                extraProps: {
                    kind: 'CONFIG',
                },
            },
        ],
    },
    {
        label: 'Validation Task Details',
        description: 'Validation is a flow to validate microtask assignment outputs',
        required: false,
        parameters: [
            {
                id: 'requiresValidation',
                label: 'Requires Validation',
                description: 'Indicates that the assignments of this task require explicit validation. If enabled, assignments will not be automatically marked as verified. Otherwise, they will be marked as verified as soon as they are submitted to the main server.',
                type: 'boolean',
                required: false,
            },
            {
                id: 'createValidationTask',
                label: 'Automatically create validation task',
                type: 'boolean',
                required: false,
                requires: [['requiresValidation', true]],
            },
            {
                id: 'useSingleValidationTask',
                label: 'Same Validation Task for Test and Main Assignments',
                description: 'If enabled, the same validation task is used for both test and main assignments',
                type: 'boolean',
                required: false,
                requires: [
                    ['requiresValidation', true],
                    ['createValidationTask', true],
                    ['enableTestTask', true],
                ],
            },
            {
                id: 'useSingleValidationTask',
                label: 'Same Validation Task for Test and Main Assignments',
                description: 'If enabled, the same validation task is used for both test and main assignments',
                type: 'fixed',
                value: true,
                required: false,
                requires: [
                    ['requiresValidation', true],
                    ['createValidationTask', true],
                    ['enableTestTask', false],
                ],
            },
            {
                id: 'validationTask.display_name',
                label: 'Validation Task Display Name',
                description: 'Name of the validation task to be displayed on the worker application to the worker',
                type: 'string',
                required: false,
                requires: [
                    ['requiresValidation', true],
                    ['createValidationTask', true],
                    ['useSingleValidationTask', true],
                ],
                requiredIfSatisfied: true,
            },
            {
                id: 'validationTask.main_display_name',
                label: 'Main Phase Validation Task Display Name',
                description: 'Name of the main phase validation task to be displayed on the worker application to the worker',
                type: 'string',
                required: false,
                requires: [
                    ['requiresValidation', true],
                    ['createValidationTask', true],
                    ['useSingleValidationTask', false],
                    ['enableTestTask', true],
                ],
                requiredIfSatisfied: true,
            },
            {
                id: 'validationTask.test_display_name',
                label: 'Test Task Display Name',
                description: 'Name of the test phase validation task to be displayed on the worker application to the worker',
                type: 'string',
                required: false,
                requires: [
                    ['requiresValidation', true],
                    ['createValidationTask', true],
                    ['useSingleValidationTask', false],
                    ['enableTestTask', true],
                ],
                requiredIfSatisfied: true,
            },
            {
                id: 'validationTask.allowEdit',
                label: 'Allow Editing',
                description: 'Allow users to edit validation microtasks',
                type: 'boolean',
                required: false,
                requires: [
                    ['requiresValidation', true],
                    ['createValidationTask', true],
                ],
            },
            {
                id: 'validationTask.allowSkipping',
                label: 'Allow Skipping',
                description: 'Allow users to skip validation microtasks',
                type: 'boolean',
                required: false,
                requires: [
                    ['requiresValidation', true],
                    ['createValidationTask', true],
                ],
            },
            {
                id: 'validationTask.baseCreditsPerMicrotask',
                label: 'Base Credits per Validation Microtask',
                description: 'This is the amount of credits a worker will get regardless of the quality of work.',
                type: 'int',
                required: false,
                requires: [
                    ['requiresValidation', true],
                    ['createValidationTask', true],
                ],
                requiredIfSatisfied: true,
            },
            {
                id: 'validationTask.spec',
                label: 'Validation Task Specification',
                description: 'A specification that gets the required configuration for the validation task',
                type: 'json_object',
                renderer: 'validation-task-builder-modal',
                required: false,
                requires: [
                    ['requiresValidation', true],
                    ['createValidationTask', true],
                ],
                requiredIfSatisfied: true,
                extraProps: {
                    kind: 'VALIDATION',
                },
            },
            {
                id: 'validationTaskAssignment.box_id',
                label: 'Choose Box for Validation Task',
                type: 'enum',
                list: {},
                required: false,
                requires: [
                    ['requiresValidation', true],
                    ['createValidationTask', true],
                ],
                requiredIfSatisfied: true,
            },
            {
                id: 'reassignRejected',
                label: 'Reassign Rejected Assignments',
                description: 'If set, rejected assignments are reassigned to the same user. This parameter is relevant only if the task requires validation.',
                type: 'boolean',
                required: false,
                requires: [['requiresValidation', true]],
            },
            {
                id: 'maxReassignmentsPerUser',
                label: 'Maximum Reassignments Per User',
                description: 'Maximum number of times a microtask can be reassigned to a user',
                type: 'int',
                default: 1,
                required: false,
                requires: [
                    ['requiresValidation', true],
                    ['reassignRejected', true],
                ],
            },
        ],
    },
    {
        label: 'Avatar Generation',
        description: 'These parameters define weather to automatically create the avatars for this task',
        required: true,
        parameters: [
            {
                id: 'generateMainTaskAvatars',
                label: 'Also Generate Avatars for Main Task',
                type: 'boolean',
                required: false,
                requires: [['createTaskAssignments', true]],
            },
            {
                id: 'mainTaskAvatarCount',
                label: 'Main Task Avatars Count',
                description: 'Number of avarars to generate for the main task',
                type: 'int',
                required: false,
                requires: [['generateMainTaskAvatars', true]],
                requiredIfSatisfied: true,
            },
            {
                id: 'avatarBoxId',
                label: 'Choose Box',
                type: 'enum',
                list: {},
                required: false,
                requires: [['generateMainTaskAvatars', true]],
                requiredIfSatisfied: true,
            },
            {
                id: 'generateValidationTaskAvatars',
                label: 'Also Generate Avatars for Validation Task',
                type: 'boolean',
                required: false,
                requires: [['requiresValidation', true]],
            },
            {
                id: 'validationTaskAvatarCount',
                label: 'Validation Task Avatars Count',
                description: 'Number of avarars to generate for the validation task',
                type: 'int',
                required: false,
                requires: [
                    ['requiresValidation', true],
                    ['generateValidationTaskAvatars', true],
                ],
                requiredIfSatisfied: true,
            },
        ],
    },
    {
        label: 'Reassignment Notifications',
        description: 'Configure the notification settings for this task, including reassignment notifications.',
        required: false,
        parameters: [
            {
                id: 'notificationOnMtReassignment.whatsappNotification',
                label: 'WhatsApp Notification',
                description: 'Send WhatsApp notification on reassignment.',
                type: 'boolean',
                required: false,
                default: false,
            },
            {
                id: 'notificationOnMtReassignment.pushNotification',
                label: 'Push Notification',
                description: 'Send push notification on reassignment.',
                type: 'boolean',
                required: false,
                default: false,
            },
        ],
    },
    {
        label: 'Verification Notifications',
        description: 'Configure the notification settings for this task, including verification notifications.',
        required: false,
        parameters: [
            {
                id: 'notificationOnMtaVerification.whatsappNotification',
                label: 'WhatsApp Notification',
                description: 'Send WhatsApp notification on verification.',
                type: 'boolean',
                required: false,
                default: false,
            },
            {
                id: 'notificationOnMtaVerification.pushNotification',
                label: 'Push Notification',
                description: 'Send push notification on verification.',
                type: 'boolean',
                required: false,
                default: false,
            },
        ],
    },
];
// Export the joi schema
const taskParameterArray = (0, parameter_specs_1.parameterArrayFromGroups)(exports.createTaskForm);
exports.createTaskSchema = (0, parameter_specs_1.joiSchema)(taskParameterArray, {
    isKeyPath: true,
}).with('maxTaskRetries', 'retryDelayDays');
//# sourceMappingURL=Create.js.map